import {FilterValuesJoinType, IFilter} from 'plugins/modules/filter/types';
import {FILTER_URL_PARAMS, FILTER_URL_PARAMS_SEPARATOR, FILTER_URL_SLUG} from 'plugins/modules/filter/constants';

const {AND, FROM, OR, TO} = FILTER_URL_PARAMS;

const SEPARATOR_AND = `${FILTER_URL_PARAMS_SEPARATOR}${AND}${FILTER_URL_PARAMS_SEPARATOR}`;
const SEPARATOR_OR = `${FILTER_URL_PARAMS_SEPARATOR}${OR}${FILTER_URL_PARAMS_SEPARATOR}`;
const SEPARATOR_FROM = `${FROM}${FILTER_URL_PARAMS_SEPARATOR}`;
const SEPARATOR_TO = `${TO}${FILTER_URL_PARAMS_SEPARATOR}`;

export class FiltersFromUrl {
    constructor(url?: string) {
        this.url = url;

        this.init();
    }

    private readonly url?: string;
    filters: IFilter[] = [];

    private getFromValue = (slug: string) => {
        const match = slug.match(/from-(\d+(?:\.\d+)?)/iu);

        return match?.[1];
    };

    private getToValue = (slug: string) => {
        const matchFrom = slug.match(/to-(\d+(?:\.\d+)?)/iu);

        return matchFrom?.[1];
    };

    private getSingleValues = (slug: string) => {
        return [slug];
    };

    private getAndValues = (slug: string) => {
        return slug.split(SEPARATOR_AND);
    };

    private getOrValues = (slug: string) => {
        return slug.split(SEPARATOR_OR);
    };

    private getRangeValues = (slug: string) => {
        const from = this.getFromValue(slug);
        const to = this.getToValue(slug);

        return [from, to];
    };

    private getUrlPath = () => {
        if (!this.url) {
            return '';
        }

        const match = this.url.match(/^[^?#]*/u);

        if (!match) {
            return '';
        }

        let path = match[0];
        path = path.replace(/\.json$/u, '');

        return path;
    };

    private getValues = (slug: string, type: FilterValuesJoinType) => {
        if (type === FilterValuesJoinType.RANGE) {
            return this.getRangeValues(slug);
        }

        if (type === FilterValuesJoinType.AND) {
            return this.getAndValues(slug);
        }

        if (type === FilterValuesJoinType.OR) {
            return this.getOrValues(slug);
        }

        return this.getSingleValues(slug);
    };

    private init = () => {
        if (!this.url) {
            return;
        }

        const filterSlugList = this.getFiltersSlugsFromUrlString();

        if (!filterSlugList) {
            return;
        }

        this.setFilters(filterSlugList);
    };

    private isAndFilter = (slug: string) => {
        return slug.includes(SEPARATOR_AND);
    };

    private isOrFilter = (slug: string) => {
        return slug.includes(SEPARATOR_OR);
    };

    private isRangeFilter = (slug: string) => {
        const isFrom = slug.includes(SEPARATOR_FROM);
        const isTo = slug.includes(SEPARATOR_TO);

        return isFrom || isTo;
    };

    private getFilterCodeAndValueString = (slug: string) => {
        const slugList = slug.split(FILTER_URL_PARAMS_SEPARATOR);
        const filterCode = slugList.shift() ?? '';
        const valueString = slugList.join(FILTER_URL_PARAMS_SEPARATOR);

        return {
            filterCode,
            valueString,
        };
    };

    private getFiltersSlugsFromUrlString = () => {
        const path = this.getUrlPath();

        const [, urlFilters] = path.split(FILTER_URL_SLUG);

        if (!urlFilters) {
            return undefined;
        }

        return urlFilters.split('/').filter(Boolean);
    };

    private getValuesJoinType = (slug: string) => {
        const isRange = this.isRangeFilter(slug);

        if (isRange) {
            return FilterValuesJoinType.RANGE;
        }

        const isOr = this.isOrFilter(slug);

        if (isOr) {
            return FilterValuesJoinType.OR;
        }

        const isAnd = this.isAndFilter(slug);

        if (isAnd) {
            return FilterValuesJoinType.AND;
        }

        return FilterValuesJoinType.SINGLE_VALUE;
    };

    private setFilters = (slugFilterList: string[]) => {
        this.filters = slugFilterList.map((slugItem) => {
            const {filterCode, valueString} = this.getFilterCodeAndValueString(slugItem);

            const type = this.getValuesJoinType(valueString);
            const values = this.getValues(valueString, type);

            return {
                code: filterCode,
                values,
                valuesJoinType: type,
            };
        });
    };
}
